import React from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, Shield } from 'lucide-react';
import { Button } from '../components/ui/button';

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="privacy-policy-page">
      <div className="max-w-3xl mx-auto px-4 py-10">
        <Button asChild variant="ghost" size="sm" className="mb-6">
          <Link to="/login" data-testid="back-to-login">
            <ChevronLeft className="w-4 h-4 mr-1" />
            Back
          </Link>
        </Button>

        <div className="flex items-center gap-3 mb-2">
          <Shield className="w-7 h-7 text-primary" />
          <h1 className="text-3xl font-bold tracking-tight">Privacy Policy</h1>
        </div>
        <p className="text-sm text-muted-foreground mb-8">
          Effective date: May 6, 2026 &middot; JobBoard by American Piping Inspection
        </p>

        <section className="space-y-6 text-sm leading-6">
          <p>
            This Privacy Policy explains how JobBoard ("we", "us", "our")
            collects, uses, and protects information when you use our personnel
            scheduling application. By using JobBoard you agree to the practices
            described below.
          </p>

          {/* MANDATORY SMS / mobile data line (Toll-Free Verification + carrier requirement) */}
          <div className="rounded-lg border-l-4 border-primary bg-primary/5 p-4">
            <h2 className="text-base font-semibold mb-2">Mobile Information Sharing</h2>
            <p className="text-sm">
              No mobile information will be shared with third parties/affiliates
              for marketing/promotional purposes; this information will not be
              shared with any third parties.
            </p>
          </div>

          <div>
            <h2 className="text-lg font-semibold mb-2">Information We Collect</h2>
            <p>
              JobBoard stores information that is necessary to provide
              scheduling, communication, and reporting services. This includes:
            </p>
            <ul className="list-disc pl-6 mt-2 space-y-1">
              <li>Account details: name, email address, phone number, role, division, and office location.</li>
              <li>Work-related records: jobs, schedules, assignments, time entries, mileage, expenses, time-off requests, and shift swap history.</li>
              <li>Notification preferences: whether you want to receive email and/or SMS notifications.</li>
              <li>Communications: messages sent in team chat channels and direct messages within the app.</li>
              <li>Limited technical data such as IP address and browser type, used only for security and troubleshooting.</li>
            </ul>
          </div>

          <div>
            <h2 className="text-lg font-semibold mb-2">How We Use Your Information</h2>
            <ul className="list-disc pl-6 space-y-1">
              <li>To assign and notify you of jobs, shift changes, and time-off decisions.</li>
              <li>To generate timesheets, payroll exports, and management reports for your employer.</li>
              <li>To send SMS and email notifications you have opted into via your Notification Settings.</li>
              <li>To secure the application against unauthorized access.</li>
            </ul>
          </div>

          <div>
            <h2 className="text-lg font-semibold mb-2">SMS Notifications &amp; Consent</h2>
            <p>
              When you provide your phone number to JobBoard, you consent to
              receive SMS notifications regarding job assignments, shift swap
              decisions, and time-off status updates. Message frequency varies
              based on your work schedule. Message and data rates may apply.
            </p>
            <p className="mt-2">
              You may opt out of SMS at any time by:
            </p>
            <ul className="list-disc pl-6 mt-1 space-y-1">
              <li>Disabling the SMS toggle in <strong>Profile &rarr; Notification Settings</strong>, or</li>
              <li>Replying <strong>STOP</strong> to any SMS we send (you can resubscribe by replying <strong>START</strong>).</li>
            </ul>
            <p className="mt-2">
              Reply <strong>HELP</strong> to receive contact information. Standard
              opt-out keywords (STOP, UNSUBSCRIBE, CANCEL, END, QUIT) are honored
              automatically.
            </p>
            <p className="mt-2">
              Full SMS program details are available in our{' '}
              <Link to="/sms-terms" className="text-primary hover:underline">
                SMS Terms &amp; Conditions
              </Link>
              .
            </p>
          </div>

          <div>
            <h2 className="text-lg font-semibold mb-2">Information Sharing</h2>
            <p>
              We do not sell or rent any user information. We share information
              only as needed to operate the service:
            </p>
            <ul className="list-disc pl-6 mt-2 space-y-1">
              <li>
                <strong>Service providers</strong> who process data on our behalf
                (Vonage for SMS delivery, Resend for email delivery, MongoDB for
                database hosting). These providers are contractually bound to use
                data only to deliver our service.
              </li>
              <li>
                <strong>Your employer</strong>, which administers your JobBoard
                account and accesses your work-related records (schedules,
                timesheets, etc.) as part of normal business operations.
              </li>
              <li>
                <strong>Legal requirements</strong>, where we are compelled to
                disclose information by valid legal process.
              </li>
            </ul>
            <p className="mt-2 font-medium">
              No mobile information will be shared with third parties/affiliates
              for marketing/promotional purposes; this information will not be
              shared with any third parties.
            </p>
          </div>

          <div>
            <h2 className="text-lg font-semibold mb-2">Data Retention</h2>
            <p>
              We retain account and work-related records for as long as your
              JobBoard account is active and for a reasonable period afterward
              to comply with legal, tax, and payroll record-keeping
              obligations. You may request deletion of your account by
              contacting your administrator.
            </p>
          </div>

          <div>
            <h2 className="text-lg font-semibold mb-2">Security</h2>
            <p>
              We use industry-standard safeguards to protect your information,
              including encrypted transport (HTTPS/TLS), hashed passwords,
              role-based access control, and audited service providers. No
              system is perfectly secure; if you suspect unauthorized access to
              your account, please contact us immediately.
            </p>
          </div>

          <div>
            <h2 className="text-lg font-semibold mb-2">Your Rights</h2>
            <p>You may at any time:</p>
            <ul className="list-disc pl-6 mt-2 space-y-1">
              <li>Access and update your profile information from the Profile page.</li>
              <li>Toggle email and SMS notifications via Notification Settings.</li>
              <li>Request a copy or deletion of your data by contacting your administrator.</li>
            </ul>
          </div>

          <div>
            <h2 className="text-lg font-semibold mb-2">Changes to This Policy</h2>
            <p>
              We may update this Privacy Policy from time to time. The
              "Effective date" above will reflect the most recent revision.
              Continued use of JobBoard after changes constitutes acceptance of
              the updated policy.
            </p>
          </div>

          <div>
            <h2 className="text-lg font-semibold mb-2">Contact</h2>
            <p>
              If you have any questions about this Privacy Policy or how we
              handle your information, contact your JobBoard administrator or
              email{' '}
              <a
                className="text-primary hover:underline"
                href="mailto:notify@apijobboard.com"
              >
                notify@apijobboard.com
              </a>
              .
            </p>
          </div>
        </section>

        <div className="mt-12 pt-6 border-t text-xs text-muted-foreground">
          &copy; {new Date().getFullYear()} American Piping Inspection &middot; JobBoard
        </div>
      </div>
    </div>
  );
}
